import { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import backArrow from '../../public/icons/ZurückIcon.svg';

import classes from './FinanceDashboard.module.css';
import NavBar from '../../components/NavBar/NavBar';
import { GET_HOST_FINANCE_DATA } from '../../graphql/queries/financeQueries';

// Fallback only for old records without stored Stripe fee fields. New bookings
// must use platformFeeAmount/hostPayoutAmount from the backend.
const PLATFORM_FEE_RATE = 0;
const PAID_PAYMENT_STATUSES = ['PAID', 'NO_REFUND', 'PARTIALLY_REFUNDED'];

const TIME_RANGES = [
  { key: '7d', label: '7 Tage' },
  { key: '30d', label: '30 Tage' },
  { key: 'month', label: 'Monat' },
  { key: 'year', label: 'Jahr' },
  { key: 'all', label: 'Alles' },
];

const formatCurrency = (amount) => {
  return amount.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

const hasCentAmount = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

const centsToEuros = (value) => Number(value) / 100;

const getBookingGross = (booking) => Number(booking?.totalPrice) || 0;

const getBookingFee = (booking) => {
  if (hasCentAmount(booking?.platformFeeAmount)) {
    return centsToEuros(booking.platformFeeAmount);
  }

  return getBookingGross(booking) * PLATFORM_FEE_RATE;
};

const getBookingNet = (booking) => {
  if (hasCentAmount(booking?.hostPayoutAmount)) {
    return centsToEuros(booking.hostPayoutAmount);
  }

  return getBookingGross(booking) - getBookingFee(booking);
};

const getFinanceDate = (booking) => {
  const dateStr = booking.createdAt || booking.startDateTime || booking.endDateTime;
  return new Date(dateStr);
};

const getDateRange = (range) => {
  const now = new Date();
  let end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;

  switch (range) {
    case '7d':
      start = new Date(now);
      start.setDate(now.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case '30d':
      start = new Date(now);
      start.setDate(now.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      break;
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
      break;
    case 'all':
    default:
      start = new Date(2023, 0, 1);
      start.setHours(0, 0, 0, 0);
      break;
  }

  return { start, end };
};

const isEligibleBooking = (b) => {
  if (PAID_PAYMENT_STATUSES.includes(b.paymentStatus)) {
    return true;
  }

  return b.status === 'COMPLETED' && !b.paymentStatus;
};

const calculateFinanceMetrics = (bookings, range) => {
  const { start, end } = getDateRange(range);

  const filtered = bookings.filter((b) => {
    if (!isEligibleBooking(b)) return false;
    const d = getFinanceDate(b);
    return d >= start && d <= end;
  });

  let totalGross = 0;
  let totalFee = 0;
  let totalNet = 0;

  filtered.forEach((b) => {
    const gross = getBookingGross(b);
    const fee = getBookingFee(b);
    const net = getBookingNet(b);

    totalGross += gross;
    totalFee += fee;
    totalNet += net;
  });

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return {
    totalGross,
    totalFee,
    totalNet,
    bookingsCount: filtered.length,
    transactions: sorted,
  };
};

const getChartData = (bookings, range) => {
  const { start, end } = getDateRange(range);
  const buckets = new Map();

  bookings.forEach((b) => {
    if (!isEligibleBooking(b)) return;
    const d = getFinanceDate(b);
    if (d < start || d > end) return;

    const net = getBookingNet(b);

    let key;
    if (range === 'year' || range === 'all') {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    } else {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      key = `${yyyy}-${mm}-${dd}`;
    }

    buckets.set(key, (buckets.get(key) || 0) + net);
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, value]) => {
      let label;
      if (range === 'year' || range === 'all') {
        const [year, month] = key.split('-').map(Number);
        const d = new Date(year, month - 1);
        label = d.toLocaleDateString('de-DE', { month: 'short' });
      } else {
        const [year, month, day] = key.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        label = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      }
      return { key, label, value };
    });
};

const MiniBarChart = ({ data }) => {
  if (!data.length) return <div className={classes.chartEmpty}>Keine Daten im Zeitraum</div>;

  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className={classes.chart}>
      {data.map((point) => {
        const height = Math.max(4, (point.value / max) * 100);
        return (
          <div key={point.key} className={classes.chartBarWrapper}>
            <div
              className={classes.chartBar}
              style={{ height: `${height}%` }}
              title={`${point.label}: ${formatCurrency(point.value)}`}
            />
            <div className={classes.chartLabel}>{point.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state?.auth || {});
  const studioOwnerID = user?.id;

  const [timeRange, setTimeRange] = useState('month');

  const { data, loading, refetch } = useQuery(GET_HOST_FINANCE_DATA, {
    variables: { studioOwnerID, limit: 1000 },
    skip: !studioOwnerID,
    fetchPolicy: 'cache-and-network',
  });

  const bookings = useMemo(
    () => data?.bookingsByStudioOwnerID?.items || [],
    [data]
  );

  const metrics = useMemo(
    () => calculateFinanceMetrics(bookings, timeRange),
    [bookings, timeRange]
  );

  const chartData = useMemo(
    () => getChartData(bookings, timeRange),
    [bookings, timeRange]
  );

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <div className={classes.page}>
      <NavBar fromFinance={true} />

      <div className={classes.navBarPhoneContainer}>
        <header className={classes.navHeader}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.4609 20.0297L10.9309 21.457L0.960938 10.5378L10.9309 0.457031L12.4017 1.9442L3.84334 10.5977L12.4609 20.0297Z"
                fill="#101010"
              />
            </svg>
          </div>
          <Link to="/" className={classes.logoLink}>
            <div className={classes.logoContainer}>
              <svg xmlns="http://www.w3.org/2000/svg" width="53" height="19" viewBox="0 0 53 19" fill="none">
                <path
                  d="M50.5002 9.5C51.8809 9.5 53.0002 8.38071 53.0002 7C53.0002 5.61929 51.8809 4.5 50.5002 4.5C49.1195 4.5 48.0002 5.61929 48.0002 7C48.0002 8.38071 49.1195 9.5 50.5002 9.5Z"
                  fill="#FF003E"
                />
                <path
                  d="M9.93692 9.78445L8.58239 9.67069C5.23081 9.39942 4.10204 9.0494 4.10204 8.2881C4.10204 7.5268 5.52603 7.10677 8.46083 7.10677C11.5606 7.10677 12.8109 7.51805 13.0714 8.62062L13.1148 8.79563H16.5185L16.4838 8.55062C16.1452 5.64542 13.5577 4.23658 8.58239 4.23658C3.60712 4.23658 1.05437 5.61917 1.05437 8.33185C1.05437 10.817 3.09483 12.0158 8.11352 12.4709L9.32912 12.5846C12.6546 12.8734 13.8876 13.3109 13.8876 14.186C13.8876 15.1398 12.3681 15.6298 9.36385 15.6298C5.96017 15.6298 4.2236 14.991 4.05863 13.6697L4.03258 13.4772H0.628906V13.7135C0.785197 16.8899 3.63317 18.5 9.10336 18.5C14.5736 18.5 16.944 16.9949 16.944 14.0285C16.944 11.3858 14.9817 10.1957 9.93692 9.78445Z"
                  fill="#1F1F1F"
                />
                <path
                  d="M42.1415 4.67403V10.3444C42.1415 13.5209 40.0663 15.4897 36.7321 15.4897C33.5976 15.4897 32.3733 14.0809 32.3733 10.4582V4.67403H21.8497V0.5L18.7152 1.83009V13.7834C18.7152 17.0823 20.2954 18.4912 24.003 18.4912C25.2968 18.4912 26.5384 18.2724 27.589 18.0537L27.7627 18.0187V15.2097L27.5022 15.2622C26.139 15.5422 25.5312 15.6473 24.5587 15.6473C22.7614 15.6473 21.8497 15.2185 21.8497 12.8646V7.2992H30.793L29.1433 8.55928V11.482C29.1433 16.2685 31.1577 18.4912 35.4731 18.4912C38.2603 18.4912 40.5525 17.5199 42.1415 15.6648V18.0537H45.3715V4.67403H42.1415Z"
                  fill="#1F1F1F"
                />
              </svg>
            </div>
          </Link>
        </header>
      </div>

      <div className={classes.pageHeader}>
        <button
          type="button"
          className={classes.backButton}
          onClick={() => navigate(-1)}
        >
          <img src={backArrow} alt="Zurück" />
        </button>
        <h1 className="titleSemibold2">Finanzen</h1>
        <button
          type="button"
          className={classes.refreshButton}
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? 'Aktualisiere…' : 'Aktualisieren'}
        </button>
      </div>

      <div className={classes.heroCard}>
        <div className={classes.heroLabel}>
          {TIME_RANGES.find((r) => r.key === timeRange)?.label}
        </div>
        <div className={classes.heroAmount}>{formatCurrency(metrics.totalNet)}</div>
        <div className={classes.heroMetaRow}>
          <div>
            <div className={classes.metaLabel}>Brutto</div>
            <div className={classes.metaValue}>{formatCurrency(metrics.totalGross)}</div>
          </div>
          <div>
            <div className={classes.metaLabel}>Gebühr</div>
            <div className={classes.metaValue}>
              -{formatCurrency(metrics.totalFee)}
            </div>
          </div>
          <div>
            <div className={classes.metaLabel}>Buchungen</div>
            <div className={classes.metaValue}>{metrics.bookingsCount}</div>
          </div>
        </div>
      </div>

      <div className={classes.rangeRow}>
        {TIME_RANGES.map((range) => (
          <button
            key={range.key}
            type="button"
            className={
              timeRange === range.key
                ? `${classes.rangeButton} ${classes.rangeButtonActive}`
                : classes.rangeButton
            }
            onClick={() => setTimeRange(range.key)}
          >
            {range.label}
          </button>
        ))}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Einnahmen-Verlauf</div>
        <MiniBarChart data={chartData} />
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>
          Transaktionen ({metrics.transactions.length})
        </div>
        {metrics.transactions.length === 0 && (
          <div className={classes.emptyText}>
            Im gewählten Zeitraum wurden keine Buchungen abgeschlossen.
          </div>
        )}
        {metrics.transactions.slice(0, 10).map((t) => {
          const net = getBookingNet(t);
          const isPaid = t.paymentStatus === 'PAID';

          return (
            <div key={t.id} className={classes.txRow}>
              <div className={classes.txLeft}>
                <div className={classes.txTitle}>
                  {t.Studio?.StudioName || 'Studio'}
                </div>
                <div className={classes.txSub}>
                  {t.projectName || 'Buchung'} · {formatDate(t.createdAt)}
                </div>
              </div>
              <div className={classes.txRight}>
                <div className={classes.txNet}>{formatCurrency(net)}</div>
                <div className={classes.txStatus}>
                  {isPaid ? 'Bezahlt' : t.status}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FinanceDashboard;
